import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LanguageIcon from '@mui/icons-material/Language';
import RemoveIcon from '@mui/icons-material/Remove';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Box, Button, Card, CardContent, Container, FormControl, GlobalStyles, IconButton, InputLabel, Menu, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import CountryList from 'country-list-with-dial-code-and-flag';
import React, { useEffect, useMemo, useState } from 'react';
import Lang, { langContext, useLang, useLanguageProvider, type Language } from '../api/Lang';
import { languageFromValue } from '../api/langConfig';
import { functions } from '../api/firebase';
import CookiesConsent from '../components/CookiesConsent';
import '../globalVars.css';
import { theme } from '../theme';

type ContactType = 'school' | 'catering' | 'parent' | 'parentAssociation' | '';

type ContactFormData = {
    from: ContactType;
    name: string;
    countryCode: string;
    mobile: string;
    email: string;
    message: string;
};

// Get all countries from the library and transform to our format
const allCountries = CountryList.getAll();

// Territories and dependencies that share dial codes with main countries
// These will be filtered out to avoid conflicts
const territoriesToExclude = new Set([
    'Guernsey',
    'Jersey',
    'Isle of Man',
    'Bermuda',
    'Gibraltar',
    'Falkland Islands',
    'British Virgin Islands',
    'Cayman Islands',
    'Montserrat',
    'Turks and Caicos Islands',
    'Anguilla',
    'American Samoa',
    'Guam',
    'Northern Mariana Islands',
    'Puerto Rico',
    'US Virgin Islands'
]);

// Map and filter countries, excluding territories that share dial codes
const countryCodes = allCountries
    .filter((country: any) => !territoriesToExclude.has(country.name))
    .map((country: any) => {
        const flag = (country.flag && typeof country.flag === 'object' && 'emoji' in country.flag)
            ? country.flag.emoji
            : (typeof country.flag === 'string' ? country.flag : '');
        return {
            code: country.dial_code,
            flag: flag,
            name: country.name
        };
    });

export default function NewLanding() {
    const [lang, setLang] = useLanguageProvider();
    const [contactForm, setContactForm] = useState<ContactFormData>({
        from: '' as ContactType,
        name: '',
        countryCode: '+30',
        mobile: '',
        email: '',
        message: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [langMenuAnchor, setLangMenuAnchor] = useState<null | HTMLElement>(null);
    const [cookiesConsented, setCookiesConsented] = useState(false);
    const [platformToggle, setPlatformToggle] = useState<'platform' | 'app'>('platform');
    const [currentSlide, setCurrentSlide] = useState(0);

    // Language strings for form labels - reactive to language changes
    const fromLabel = useMemo(() => useLang('Οργανισμός|Organization', lang), [lang]);
    const nameLabel = useMemo(() => useLang('Όνομα|Name', lang), [lang]);
    const mobileLabel = useMemo(() => useLang('Κινητό (Προαιρετικό)|Mobile (Optional)', lang), [lang]);
    const mobileNumberPlaceholder = useMemo(() => useLang('Αριθμός|Number', lang), [lang]);
    const emailLabel = useMemo(() => useLang('Email|Email', lang), [lang]);
    const messageLabel = useMemo(() => useLang('Μήνυμα (Προαιρετικό)|Message (Optional)', lang), [lang]);
    const sendButton = useMemo(() => useLang('Αποστολή|Send', lang), [lang]);
    const sendingButton = useMemo(() => useLang('Αποστολή...|Sending...', lang), [lang]);

    // Handle language change
    const handleLanguageChange = (newLang: Language) => {
        setLang(newLang);
        // Dispatch event for HTML page compatibility
        window.dispatchEvent(new CustomEvent('languageChanged', { detail: { lang: newLang } }));
        setLangMenuAnchor(null);
    };

    // Load saved language preference on mount
    useEffect(() => {
        const savedLang = localStorage.getItem('lang');
        if (savedLang) {
            setLang(languageFromValue(savedLang));
        }
    }, [setLang]);

    const handleLangMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setLangMenuAnchor(event.currentTarget);
    };

    const handleLangMenuClose = () => {
        setLangMenuAnchor(null);
    };

    // Listen for language changes from the HTML page
    useEffect(() => {
        const handleLanguageChange = (event: CustomEvent) => {
            setLang(event.detail.lang);
        };

        window.addEventListener('languageChanged', handleLanguageChange as EventListener);

        return () => {
            window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
        };
    }, [setLang]);

    // Auto-rotate image slider
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % 3);
        }, 3000); // Change slide every 3 seconds

        return () => clearInterval(interval);
    }, []);

    const handleContactFormChange = (field: keyof ContactFormData, value: string) => {
        setContactForm(prev => ({ ...prev, [field]: value }));
        setSubmitError(null);
        setSubmitSuccess(false);
    };

    const handleContactSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setSubmitError(null);
        setSubmitSuccess(false);

        try {
            // Validate required fields
            if (!contactForm.from || !contactForm.name.trim() || !contactForm.email.trim()) {
                setSubmitError(lang === 'el'
                    ? 'Παρακαλώ συμπληρώστε τα υποχρεωτικά πεδία'
                    : 'Please fill in all required fields');
                setSubmitting(false);
                return;
            }

            // Validate email format
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(contactForm.email)) {
                setSubmitError(lang === 'el'
                    ? 'Παρακαλώ εισάγετε έγκυρο email'
                    : 'Please enter a valid email address');
                setSubmitting(false);
                return;
            }

            const submitContactForm = functions.httpsCallable('submitContactForm');
            await submitContactForm({
                from: contactForm.from,
                name: contactForm.name,
                mobile: contactForm.mobile ? `${contactForm.countryCode}${contactForm.mobile}` : undefined,
                email: contactForm.email,
                message: contactForm.message || undefined,
            });

            setSubmitSuccess(true);
            setContactForm({
                from: '' as ContactType,
                name: '',
                countryCode: '+30',
                mobile: '',
                email: '',
                message: '',
            });
        } catch (error) {
            console.error('Error submitting contact form:', error);
            Sentry.captureException(error as Error);
            setSubmitError(lang === 'el'
                ? 'Σφάλμα κατά την αποστολή. Παρακαλώ δοκιμάστε ξανά.'
                : 'Error submitting form. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const schoolLogos = [
        {
            name: 'Athens College',
            logo: 'logo-ac.png',
            landingUrl: 'https://ac.lunch-box.gr/school-landing',
        },
        {
            name: 'ACS Athens',
            logo: 'logo-acs.png',
            landingUrl: 'https://acs.lunch-box.gr/school-landing',
        },
        {
            name: 'St. Catherine\'s',
            logo: 'logo-stc.png',
            landingUrl: 'https://stc.lunch-box.gr/school-landing',
        },
        {
            name: 'Moraitis School',
            logo: 'logo-moraitis.png',
            landingUrl: 'https://moraitis.lunch-box.gr/school-landing',
        },
        {
            name: 'Campion School',
            logo: 'logo-campion.png',
            landingUrl: 'https://campion.lunch-box.gr/school-landing',
        },
    ];

    return (
        <langContext.Provider value={[lang, setLang]}>
            <ThemeProvider theme={theme}>
                <GlobalStyles
                    styles={{
                        'html, body': {
                            margin: 0,
                            padding: 0,
                        },
                        '.MuiButton-root': {
                            fontFamily: 'Roboto, Helvetica, Arial, sans-serif !important',
                            '& .MuiButton-label': {
                                fontFamily: 'Roboto, Helvetica, Arial, sans-serif !important',
                            }
                        },
                        button: {
                            fontFamily: 'Roboto, Helvetica, Arial, sans-serif !important',
                        },
                        '.MuiTextField-root': {
                            fontFamily: 'Roboto, Helvetica, Arial, sans-serif !important',
                        },
                        '.MuiInputBase-root': {
                            fontFamily: 'Roboto, Helvetica, Arial, sans-serif !important',
                            '& input': {
                                fontFamily: 'Roboto, Helvetica, Arial, sans-serif !important',
                            },
                            '& textarea': {
                                fontFamily: 'Roboto, Helvetica, Arial, sans-serif !important',
                            }
                        },
                        '.MuiInputLabel-root': {
                            fontFamily: 'Roboto, Helvetica, Arial, sans-serif !important',
                        },
                        '.MuiSelect-root': {
                            fontFamily: 'Roboto, Helvetica, Arial, sans-serif !important',
                        },
                        '.MuiMenuItem-root': {
                            fontFamily: 'Roboto, Helvetica, Arial, sans-serif !important',
                        },
                        '.MuiFormControl-root': {
                            fontFamily: 'Roboto, Helvetica, Arial, sans-serif !important',
                        },
                        input: {
                            fontFamily: 'Roboto, Helvetica, Arial, sans-serif !important',
                        },
                        textarea: {
                            fontFamily: 'Roboto, Helvetica, Arial, sans-serif !important',
                        },
                        select: {
                            fontFamily: 'Roboto, Helvetica, Arial, sans-serif !important',
                        },
                        label: {
                            fontFamily: 'Roboto, Helvetica, Arial, sans-serif !important',
                        }
                    }}
                />
                <Box sx={{ minHeight: '100vh', bgcolor: '#ffffff', fontFamily: 'Roboto, sans-serif', overflowX: 'hidden', width: '100%' }}>
                    {/* Language Switcher - Fixed Position Top Right */}
                    <Box
                        sx={{
                            position: 'fixed',
                            top: { xs: 16, md: 24 },
                            right: { xs: 16, md: 24 },
                            zIndex: 1000,
                        }}
                    >
                        <IconButton
                            onClick={handleLangMenuOpen}
                            sx={{
                                bgcolor: 'white',
                                color: '#01ABAE',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                '&:hover': {
                                    bgcolor: '#f5f5f5',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                },
                            }}
                        >
                            <LanguageIcon />
                        </IconButton>
                        <Menu
                            anchorEl={langMenuAnchor}
                            open={Boolean(langMenuAnchor)}
                            onClose={handleLangMenuClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            <MenuItem
                                onClick={() => handleLanguageChange('el')}
                                selected={lang === 'el'}
                                sx={{
                                    color: lang === 'el' ? '#01ABAE' : 'inherit',
                                    fontWeight: lang === 'el' ? 600 : 400,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box component="img" src="/img-assets/el.jpg" alt="Greek" sx={{ width: 20, height: 15, borderRadius: '2px' }} />
                                    <Typography>Ελληνικά</Typography>
                                </Box>
                            </MenuItem>
                            <MenuItem
                                onClick={() => handleLanguageChange('en')}
                                selected={lang === 'en'}
                                sx={{
                                    color: lang === 'en' ? '#01ABAE' : 'inherit',
                                    fontWeight: lang === 'en' ? 600 : 400,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box component="img" src="/img-assets/en.png" alt="English" sx={{ width: 20, height: 15, borderRadius: '2px' }} />
                                    <Typography>English</Typography>
                                </Box>
                            </MenuItem>
                        </Menu>
                    </Box>

                    {/* Header with Logo */}
                    <Box
                        sx={{
                            bgcolor: '#ffffff',
                            py: '20px',
                            borderBottom: '1px solid #e5e7eb',
                            position: 'sticky',
                            top: 0,
                            zIndex: 100,
                        }}
                    >
                        <Container maxWidth="lg">
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Box
                                    component="img"
                                    src="/img-assets/logo-light.svg"
                                    alt="Lunchbox"
                                    sx={{
                                        height: '50px',
                                        width: 'auto',
                                    }}
                                />
                            </Box>
                        </Container>
                    </Box>
                    {/* Hero Section */}
                    <Box
                        sx={{
                            width: '100%',
                            bgcolor: '#F5F7FA',
                            pt: { xs: 6, md: 8, lg: 10 },
                            pb: 0,
                            px: 0,
                            overflow: 'visible',
                            position: 'relative',
                        }}
                    >
                        <Container
                            maxWidth="lg"
                            sx={{
                                overflow: 'visible',
                                position: 'relative',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    alignItems: { xs: 'center', md: 'center' },
                                    gap: { xs: 4, md: 6, lg: 8 },
                                    overflow: 'visible',
                                    position: 'relative',
                                    minHeight: { xs: 'auto', md: '400px' },
                                }}
                            >
                                {/* Left Side: Text and Button */}
                                <Box
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: { xs: 'center', md: 'flex-start' },
                                        textAlign: { xs: 'center', md: 'left' },
                                        alignSelf: { xs: 'center', md: 'flex-start' },
                                        pt: { xs: 0, md: 0, lg: '50px' },
                                    }}
                                >
                                    <Typography
                                        variant="h1"
                                        component="h1"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: '1.5rem', md: '2rem', lg: '2.5rem' },
                                            color: '#1a1a1a',
                                            mb: { xs: 2, md: 3 },
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        <Lang>Ψηφιακή διαχείριση σχολικών γευμάτων|Digital Meal Management for Schools</Lang>
                                    </Typography>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontSize: { xs: '0.8rem', md: '1rem', lg: '1.2rem' },
                                            fontWeight: 400,
                                            color: '#1a1a1a',
                                            mb: { xs: 3, md: 4 },
                                            maxWidth: '600px',
                                            lineHeight: 1.5,
                                        }}
                                    >
                                        <Lang>Μειώστε διοικητική εργασία, οργανώστε εύκολα τις παραγγελίες και διασφαλίστε συμμόρφωση με διατροφικές απαιτήσεις.|Reduce administrative workload, streamline meal ordering, and ensure dietary compliance.</Lang>
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        href="#contact"
                                        sx={{
                                            bgcolor: '#E35827',
                                            color: 'white',
                                            px: 2.75,
                                            py: 2.25,
                                            fontSize: { xs: '0.9rem', md: '0.95rem' },
                                            fontWeight: 600,
                                            borderRadius: '16px',
                                            textTransform: 'none',
                                            alignSelf: { xs: 'center', md: 'flex-start' },
                                            boxShadow: 'none',
                                            '&:hover': {
                                                bgcolor: '#c04a1f',
                                                boxShadow: 'none',
                                            },
                                        }}
                                    >
                                        <Lang>Επικοινωνήστε μαζί μας|Contact us</Lang>
                                    </Button>
                                </Box>

                                {/* Right Side: Hero Image */}
                                <Box
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'flex-end',
                                        position: 'relative',
                                        overflow: 'visible',
                                        mb: { xs: '-55px', md: '-46px', lg: '-49px' },
                                        alignSelf: { xs: 'center', md: 'flex-end' },
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={`/img-assets/hero-${lang}.svg`}
                                        alt="Lunchbox Platform"
                                        sx={{
                                            width: '100%',
                                            maxWidth: { xs: '100%', md: '750px', lg: '850px' },
                                            height: 'auto',
                                            filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1))',
                                            display: 'block',
                                            position: 'relative',
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Container>
                    </Box>

                    {/* Schools Section - Horizontal Scrolling */}
                    <Box sx={{ bgcolor: '#ffffff', py: '60px', mt: 6 }}>
                        <Container maxWidth="lg">
                            <Typography
                                sx={{
                                    textAlign: 'center',
                                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                                    fontWeight: 500,
                                    color: '#374151',
                                    letterSpacing: '0.02em',
                                    py: 4,
                                    mb: 2,
                                }}
                            >
                                <Lang>Μας εμπιστεύονται κορυφαία ιδιωτικά και διεθνή σχολεία στην Ελλάδα.|Trusted by leading private and international schools in Greece.</Lang>
                            </Typography>
                            <Box sx={{
                                width: '100%',
                                overflow: 'hidden',
                                position: 'relative',
                                '&::before, &::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    width: '100px',
                                    height: '100%',
                                    zIndex: 1,
                                    pointerEvents: 'none',
                                },
                                '&::before': {
                                    left: 0,
                                    background: 'linear-gradient(to right, #ffffff, transparent)',
                                },
                                '&::after': {
                                    right: 0,
                                    background: 'linear-gradient(to left, #ffffff, transparent)',
                                },
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    gap: { xs: 4, md: 6 },
                                    width: 'fit-content',
                                    animation: 'scroll 30s linear infinite',
                                    '@keyframes scroll': {
                                        '0%': {
                                            transform: 'translateX(0)',
                                        },
                                        '100%': {
                                            transform: 'translateX(calc(-100px * 5 - 1rem * 4))',
                                        },
                                    },
                                    '@media (min-width: 900px)': {
                                        '@keyframes scroll': {
                                            '0%': {
                                                transform: 'translateX(0)',
                                            },
                                            '100%': {
                                                transform: 'translateX(calc(-140px * 5 - 1.5rem * 4))',
                                            },
                                        },
                                    },
                                }}>
                                    {schoolLogos.map((school) => (
                                        <Box
                                            key={school.name}
                                            component="a"
                                            href={school.landingUrl}
                                            aria-label={`${school.name} landing page`}
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                flexShrink: 0,
                                                textDecoration: 'none',
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={`/img-assets/school-logos/${school.logo}`}
                                                alt={school.name}
                                                sx={{
                                                    height: { xs: 50, md: 60 },
                                                    width: 'auto',
                                                    maxWidth: { xs: '100px', md: '140px' },
                                                    objectFit: 'contain',
                                                    opacity: 0.8,
                                                    transition: 'opacity 0.3s ease',
                                                    '&:hover': {
                                                        opacity: 1,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    ))}
                                    {/* Duplicate logos for seamless loop */}
                                    {schoolLogos.map((school) => (
                                        <Box
                                            key={`duplicate-${school.name}`}
                                            component="a"
                                            href={school.landingUrl}
                                            aria-label={`${school.name} landing page`}
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                flexShrink: 0,
                                                textDecoration: 'none',
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={`/img-assets/school-logos/${school.logo}`}
                                                alt={school.name}
                                                sx={{
                                                    height: { xs: 50, md: 60 },
                                                    width: 'auto',
                                                    maxWidth: { xs: '100px', md: '140px' },
                                                    objectFit: 'contain',
                                                    opacity: 0.8,
                                                    transition: 'opacity 0.3s ease',
                                                    '&:hover': {
                                                        opacity: 1,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Container>
                    </Box>

                    {/* How We Do It Section */}
                    <Box sx={{ bgcolor: '#ffffff', pb: { xs: 8, md: 12 } }}>
                        <Container maxWidth="lg">
                            <Box sx={{ textAlign: 'center', mb: 8 }}>
                                <Typography
                                    variant="h2"
                                    component="h2"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '1.3rem', md: '1.75rem', lg: '2.2rem' },
                                        mb: 2,
                                        color: '#1a1a1a',
                                    }}
                                >
                                    <Lang>Πώς το Κάνουμε|How We Do It</Lang>
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: '#6b7280',
                                        width: { xs: '100%', sm: '640px', lg: '980px' },
                                        mx: 'auto',
                                        fontWeight: 400,
                                    }}
                                >
                                    <Lang>
                                        Οι λειτουργίες και τα χαρακτηριστικά της πλατφόρμας μας, προσαρμοσμένα στις ανάγκες του κάθε σχολείου και εταιρείας σίτισης|The features and capabilities of our platform, tailored specifically to the needs of each school and catering company
                                    </Lang>
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 5,
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                            }}>
                                {[
                                    {
                                        title: 'Καθημερινά Γεύματα|Daily Meals',
                                        description: 'Οι γονείς παραγγέλνουν γεύματα και σνακ για κάθε διάλειμμα, μέσω της ηλεκτρονικής πλατφόρμας μας.|Parents order meals and snacks for every school break through our electronic platform.',
                                        image: 'daily-meals.svg',
                                    },
                                    {
                                        title: 'Πακέτα Σίτισης|Meal Plans',
                                        description: 'Επιλογή για αγορά πακέτων σίτισης (ετήσια, μηνιαία, κτλ.) με έκπτωση για αδέλφια και προσωπικό.|Ability to purchase meal plans (annual, monthly, etc.) and discounts for siblings and staff.',
                                        image: 'meal-packages.svg',
                                    },
                                    {
                                        title: 'Αλλαγές / Ακυρώσεις Παραγγελιών|Order Changes / Cancellations',
                                        description: 'Οι γονείς μπορούν να αλλάξουν ή να ακυρώσουν τις παραγγελίες για τα παιδιά τους μέσω της πλατφόρμας άμεσα.|Parents can change or cancel the orders for their children through the platform instantly.',
                                        image: 'change-cancel-orders.svg',
                                    },
                                    {
                                        title: 'Λογαριασμός Γονέα/Μαθητή|Parent/Student Account',
                                        description: 'Κάθε γονέας έχει τον δικό του λογαριασμό για να διαχειρίζεται τις παραγγελίες των παιδιών του.|Each parent has their own account to manage their children\'s orders.',
                                        image: 'parents-account.svg',
                                    },
                                    {
                                        title: 'e-Πληρωμές|e-Payments',
                                        description: 'Οι πληρωμές γίνονται online, χωρίς την ανάγκη για μετρητά από τους μαθητές.|Payments are processed online, eliminating the need for cash from students.',
                                        image: 'e-payments.svg',
                                    },
                                    {
                                        title: 'Ασφάλεια & Συμμόρφωση GDPR|Data Security & GDPR Compliance',
                                        description: 'Πλήρης συμμόρφωση με τον GDPR και υψηλά πρότυπα ασφάλειας στη διαχείριση δεδομένων.|Full compliance with GDPR and high security standards in data management.',
                                        image: 'gdpr.svg',
                                    },
                                ].map((feature, index) => (
                                    <Box key={index} sx={{ position: 'relative' }}>
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '-15px',
                                                left: '-20px',
                                                width: 80,
                                                height: 80,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: 'white',
                                                transform: 'rotate(-4deg)',
                                                zIndex: 2,
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src={`/img-assets/${feature.image}`}
                                                alt={feature.title.split('|')[0]}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'contain',
                                                }}
                                            />
                                        </Box>
                                        <Box
                                            sx={{
                                                width: '300px',
                                                height: '300px',
                                                position: 'relative',
                                                borderRadius: '16px',
                                                bgcolor: 'white',
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    inset: 0,
                                                    borderRadius: '16px',
                                                    padding: '4px',
                                                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(1, 171, 174, 0.3) 100%)',
                                                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                                    WebkitMaskComposite: 'xor',
                                                    maskComposite: 'exclude',
                                                    zIndex: 1,
                                                },
                                                '&:hover': {
                                                    boxShadow: '0 10px 25px rgba(1, 171, 174, 0.1)',
                                                    transform: 'translateY(-4px)',
                                                    '&::before': {
                                                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(1, 171, 174, 0.3) 100%)',
                                                    },
                                                },
                                            }}
                                        >
                                            <Card
                                                elevation={0}
                                                sx={{
                                                    width: '100%',
                                                    height: '100%',
                                                    borderRadius: '12px',
                                                    bgcolor: 'transparent',
                                                    pt: 10,
                                                    transition: 'all 0.3s ease',
                                                    position: 'relative',
                                                    zIndex: 0,
                                                }}
                                            >
                                                <CardContent sx={{ p: 3 }}>
                                                    {feature.description != null ? (
                                                        <>
                                                            <Typography
                                                                variant="h5"
                                                                gutterBottom
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: '#1a1a1a',
                                                                    mb: 2,
                                                                }}
                                                            >
                                                                <Lang>{feature.title}</Lang>
                                                            </Typography>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    color: '#6b7280',
                                                                    lineHeight: 1.6,
                                                                }}
                                                            >
                                                                <Lang>{feature.description}</Lang>
                                                            </Typography>
                                                        </>
                                                    ) : (
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                color: '#6b7280',
                                                                lineHeight: 1.6,
                                                            }}
                                                        >
                                                            <Lang>{feature.title}</Lang>
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Container>
                    </Box>

                    {/* Stats Section - Shopify Style */}
                    <Box sx={{ bgcolor: '#01ABAE', py: { xs: 3, md: 4 }, borderBottom: '1px solid #e5e7eb' }}>
                        <Container maxWidth="lg">
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: { xs: 2, md: 4 },
                                textAlign: 'center',
                                justifyContent: 'center',
                            }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h2" sx={{
                                        fontWeight: 700,
                                        color: '#D9F2F3',
                                        mb: 1,
                                        fontSize: { xs: '1.5rem', md: '2.5rem' }
                                    }}>
                                        <Lang>3000+|3000+</Lang>
                                    </Typography>
                                    <Typography variant="h6" sx={{
                                        color: '#FFFFFF',
                                        fontWeight: 500,
                                        fontSize: { xs: '0.75rem', md: '1.25rem' }
                                    }}>
                                        <Lang>Μαθητές|Students</Lang>
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h2" sx={{
                                        fontWeight: 700,
                                        color: '#D9F2F3',
                                        mb: 1,
                                        fontSize: { xs: '1.5rem', md: '2.5rem' }
                                    }}>
                                        <Lang>200+|200+</Lang>
                                    </Typography>
                                    <Typography variant="h6" sx={{
                                        color: '#FFFFFF',
                                        fontWeight: 500,
                                        fontSize: { xs: '0.75rem', md: '1.25rem' }
                                    }}>
                                        <Lang>Προσωπικό|Personnel</Lang>
                                    </Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="h2" sx={{
                                        fontWeight: 700,
                                        color: '#D9F2F3',
                                        mb: 1,
                                        fontSize: { xs: '1.5rem', md: '2.5rem' }
                                    }}>
                                        <Lang>50Κ+|50Κ+</Lang>
                                    </Typography>
                                    <Typography variant="h6" sx={{
                                        color: '#FFFFFF',
                                        fontWeight: 500,
                                        fontSize: { xs: '0.75rem', md: '1.25rem' },
                                        display: { xs: 'none', sm: 'block' }
                                    }}>
                                        <Lang>Ολοκληρωμένες Παραγγελίες|Completed Orders</Lang>
                                    </Typography>
                                    <Typography variant="h6" sx={{
                                        color: '#FFFFFF',
                                        fontWeight: 500,
                                        fontSize: { xs: '0.75rem', md: '1.25rem' },
                                        display: { xs: 'block', sm: 'none' }
                                    }}>
                                        <Lang>Παραγγελίες|Orders</Lang>
                                    </Typography>
                                </Box>
                            </Box>
                        </Container>
                    </Box>

                    {/* What We Do Section - Shopify Style */}
                    {/* <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
                        <Box sx={{ textAlign: 'center', mb: 8 }}>
                            <Typography
                                variant="h2"
                                component="h2"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.3rem', md: '1.75rem', lg: '2.2rem' },
                                    mb: 2,
                                    color: '#1a1a1a',
                                }}
                            >
                                <Lang>Τι Κάνουμε|What We Do</Lang>
                            </Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: '#6b7280',
                                    width: '100%',
                                    fontWeight: 400,
                                }}
                            >
                                <Lang>
                                Οι λειτουργίες και τα χαρακτηριστικά της πλατφόρμας μας, προσαρμοσμένα στις ανάγκες του κάθε σχολείου και εταιρείας Catering|The features and capabilities of our platform, tailored specifically to the needs of each school and Catering company
                                </Lang>
                            </Typography>
                        </Box>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                            gap: 4,
                        }}>
                            {[
                                {
                                    title: 'Βελτιώνουμε την Εμπειρία των Γονέων|We Elevate Parents\' Experience',
                                    description: 'Κάνουμε την παραγγελία γευμάτων απλή και άνετη για τους γονείς. Διαχειρίζονται όλες τις παραγγελίες των παιδιών τους από έναν λογαριασμό, πληρώνουν online και έχουν πλήρη έλεγχο.|We make meal ordering simple and convenient for parents. They manage all their children\'s orders from one account, pay online, and have full control.',
                                },
                                {
                                    title: 'Σύγχρονα Σχολεία|We Modernize Schools',
                                    description: 'Παρέχουμε σύγχρονη τεχνολογία που απλοποιεί τη διαχείριση παραγγελιών, μειώνει τη γραφειοκρατία και βελτιώνει την διαδικασία της σίτισης των μαθητών κατά τη διάρκεια των διαλειμμάτων.|We provide modern technology that simplifies the management of meal ordering, reduces paperwork, and improves students\' experience during lunch breaks.',
                                },
                                {
                                    title: 'Υποστήριξη Εταιρειών Σίτισης|We Support Catering Companies',
                                    description: 'Διαχειριζόμαστε όλα τα τεχνικά και λειτουργικά θέματα, ώστε οι εταιρείες σίτισης να μπορούν να εστιάσουν στην παραγωγή υγιεινού φαγητού. Παρέχουμε πλήρη υποστήριξη για διεπαφές.|We handle all technical and operational aspects, so catering companies can focus on producing healthy food. We provide full support for integration.',
                                },
                            ].map((feature, index) => (
                                <Box key={index}>
                                    <Card
                                        elevation={0}
                                        sx={{
                                            height: '100%',
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '12px',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                boxShadow: '0 10px 25px rgba(1, 171, 174, 0.1)',
                                                transform: 'translateY(-4px)',
                                                borderColor: '#01ABAE',
                                            },
                                        }}
                                    >
                                        <CardContent sx={{ p: 4 }}>
                                            <Box
                                                sx={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: '12px',
                                                    bgcolor: '#01ABAE',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    mb: 3,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        borderRadius: '4px',
                                                        bgcolor: 'white',
                                                    }}
                                                />
                                            </Box>
                                            <Typography
                                                variant="h5"
                                                gutterBottom
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#1a1a1a',
                                                    mb: 2,
                                                }}
                                            >
                                                <Lang>{feature.title}</Lang>
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: '#6b7280',
                                                    lineHeight: 1.6,
                                                }}
                                            >
                                                <Lang>{feature.description}</Lang>
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Box>
                            ))}
                        </Box>
                    </Container> */}

                    {/* Platform/App Toggle Section */}
                    <Box sx={{ bgcolor: 'rgba(227, 88, 39, 0.08)', py: { xs: 8, md: 12 } }}>
                        <Container maxWidth="lg">
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '40px',
                                mb: { xs: 4, md: 6 },
                            }}>
                                {/* Title and Toggle Section */}
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '32px',
                                    alignItems: 'center',
                                }}>
                                    <Typography
                                        variant="h2"
                                        component="h2"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: '1.3rem', md: '1.75rem', lg: '2.2rem' },
                                            color: '#1f2937',
                                            textAlign: 'center',
                                            width: '100%',
                                        }}
                                    >
                                        <Lang>Οι ηλεκτρονικές υπηρεσίες μας|Our web based solutions</Lang>
                                    </Typography>

                                    {/* Toggle Switch */}
                                    <Box sx={{
                                        display: 'flex',
                                        bgcolor: '#f8f9fa',
                                        borderRadius: '20px',
                                        p: '2px',
                                        gap: '4px',
                                        width: { xs: 'calc(100% - 16px)', sm: '400px', md: '420px' },
                                        maxWidth: '100%',
                                        height: '54px',
                                        position: 'relative',
                                    }}>
                                        {/* Sliding indicator */}
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '2px',
                                                bottom: '2px',
                                                left: platformToggle === 'platform' ? '2px' : 'calc(50% + 2px)',
                                                right: platformToggle === 'platform' ? 'calc(50% + 2px)' : '2px',
                                                bgcolor: '#E35827',
                                                borderRadius: '18px',
                                                transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1), right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                zIndex: 0,
                                            }}
                                        />
                                        <Box
                                            onClick={() => setPlatformToggle('platform')}
                                            sx={{
                                                flex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '18px',
                                                color: platformToggle === 'platform' ? '#ffffff' : '#1f2937',
                                                cursor: 'pointer',
                                                transition: 'color 0.3s ease',
                                                fontWeight: 500,
                                                fontSize: { xs: '13px', sm: '14px', md: '16px' },
                                                position: 'relative',
                                                zIndex: 1,
                                            }}
                                        >
                                            <Lang>Πλατφόρμα Διαχείρισης|Admin Platform</Lang>
                                        </Box>
                                        <Box
                                            onClick={() => setPlatformToggle('app')}
                                            sx={{
                                                flex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                borderRadius: '18px',
                                                color: platformToggle === 'app' ? '#ffffff' : '#1f2937',
                                                cursor: 'pointer',
                                                transition: 'color 0.3s ease',
                                                fontWeight: 500,
                                                fontSize: { xs: '13px', sm: '14px', md: '16px' },
                                                position: 'relative',
                                                zIndex: 1,
                                            }}
                                        >
                                            <Lang>Εφαρμογή Γονέα|Parent App</Lang>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Screenshots and Content Section */}
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    gap: { xs: 3, md: 5 },
                                    alignItems: { xs: 'flex-start', md: 'center' },
                                }}>
                                    {/* Left: Image Slider */}
                                    <Box sx={{
                                        flexShrink: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                    }}>
                                        <Box sx={{
                                            width: { xs: '100%', md: '540px' },
                                            height: { xs: '240px', md: '360px' },
                                            position: 'relative',
                                            overflow: 'hidden',
                                            p: 0,
                                            m: 0,
                                        }}>
                                            {/* Slider Images */}
                                            <Box sx={{
                                                display: 'flex',
                                                width: '300%',
                                                height: '100%',
                                                transform: `translateX(-${currentSlide * 33.333}%)`,
                                                transition: 'transform 0.5s ease-in-out',
                                                p: 0,
                                                m: 0,
                                            }}>
                                                {[0, 1, 2].map((index) => (
                                                    <Box
                                                        key={index}
                                                        component="img"
                                                        src={`/img-assets/slider${index + 1}-${platformToggle === 'platform' ? 'admin' : 'app'}-${lang}.svg`}
                                                        alt={`Slide ${index + 1}`}
                                                        sx={{
                                                            width: '33.333%',
                                                            height: '100%',
                                                            flexShrink: 0,
                                                            objectFit: 'fill',
                                                            borderRadius: '10px',
                                                            display: 'block',
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>

                                        {/* Pagination Dots */}
                                        <Box sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            gap: '8px',
                                        }}>
                                            {[0, 1, 2].map((index) => (
                                                <Box
                                                    key={index}
                                                    onClick={() => setCurrentSlide(index)}
                                                    sx={{
                                                        width: '8px',
                                                        height: '8px',
                                                        borderRadius: '50%',
                                                        bgcolor: currentSlide === index ? '#E35827' : 'rgba(0, 0, 0, 0.3)',
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.3s ease',
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>

                                    {/* Right: Text Content */}
                                    <Box sx={{
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 3,
                                    }}>
                                        {/* Title and Subtitle */}
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                        }}>
                                            <Typography
                                                variant="h3"
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: { xs: '1.5rem', md: '2rem' },
                                                    color: '#000000',
                                                    lineHeight: 1.2,
                                                }}
                                            >
                                                {platformToggle === 'platform' ? (
                                                    <Lang>Για σχολεία|For Schools</Lang>
                                                ) : (
                                                    <Lang>Για γονείς και οικογένειες|For parents and families</Lang>
                                                )}
                                            </Typography>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 400,
                                                    fontSize: { xs: '0.9rem', md: '1.05rem' },
                                                    color: '#000000',
                                                }}
                                            >
                                                {platformToggle === 'platform' ? (
                                                    <><Lang>Το LunchBox είναι μια ψηφιακή πλατφόρμα για τη διαχείριση σχολικών γευμάτων.|LunchBox is a digital infrastructure platform for school meal management.</Lang>
                                                        <br />
                                                        <Lang>Συνεργαζόμαστε με σχολεία που έχουν εσωτερικές κουζίνες ή εξωτερικούς παρόχους σίτισης.|We work with schools that have in-house kitchens or external catering partners.</Lang></>
                                                ) : (
                                                    <Lang>Οι γονείς μπορούν να παραγγέλνουν γεύματα, να διαχειρίζονται τις παραγγελίες των παιδιών τους και να πληρώνουν online.|Parents can order meals, manage their children's orders and pay online.</Lang>
                                                )}
                                            </Typography>
                                        </Box>

                                        {/* Feature Points */}
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 3,
                                        }}>
                                            {platformToggle === 'platform' ? (
                                                <>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: { xs: '1rem', md: '1.1rem' },
                                                            color: '#000000',
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        <Lang>Τι κερδίζουν τα σχολεία:|What schools gain:</Lang>
                                                    </Typography>
                                                    {([
                                                        { title: 'Κεντρική ημερήσια διαχείριση γευμάτων|Centralised daily meal management', desc: 'Πλήρης ορατότητα όλων των παραγγελιών ανά τμήματα και σχολικά έτη — χωρίς χειροκίνητη επεξεργασία.|Full visibility of all orders across classes and year groups — no manual reconciliation.' },
                                                        { title: 'Έλεγχος αλλεργιών και διατροφικής συμμόρφωσης|Allergy and dietary compliance control', desc: 'Αυτόματη σήμανση παραγγελιών βάσει του διατροφικού προφίλ κάθε μαθητή.|Automatic flagging of orders based on each student\'s dietary profile.' },
                                                        { title: 'Μείωση του διοικητικού χρόνου|Administrative time reduction', desc: 'Κατάργηση υπολογιστικών φύλλων, χειρισμού μετρητών και χειροκίνητης καταγραφής παραγγελιών.|Eliminate spreadsheets, cash handling and manual order tracking.' },
                                                        { title: 'Αναφορές παραγωγής και οικονομικών σε πραγματικό χρόνο|Real-time production & financial reporting', desc: 'Ακριβής πρόβλεψη για τον σχεδιασμό κουζίνας και τον εποπτικό έλεγχο προϋπολογισμού.|Accurate forecasting for kitchen planning and budget oversight.' },
                                                    ]).map((item, index) => (
                                                        <Box key={index} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                                            <Box
                                                                component="img"
                                                                src="/img-assets/double-check.svg"
                                                                alt="check"
                                                                sx={{ width: '20px', height: '20px', flexShrink: 0 }}
                                                            />
                                                            <Box>
                                                                <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.95rem', md: '1rem' }, color: '#000000', mb: 0.5 }}>
                                                                    <Lang>{item.title}</Lang>
                                                                </Typography>
                                                                <Typography sx={{ fontSize: { xs: '0.875rem', md: '0.95rem' }, color: '#4b5563', lineHeight: 1.5 }}>
                                                                    <Lang>{item.desc}</Lang>
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </>
                                            ) : (
                                                [
                                                    'Παραγγελία γευμάτων και πακέτα σίτισης|Order meals and meal plans',
                                                    'Αλλαγές και ακυρώσεις παραγγελιών|Order changes and cancellations',
                                                    'Ηλεκτρονικές πληρωμές|Electronic payments',
                                                ].map((text, index) => (
                                                    <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                        <Box
                                                            component="img"
                                                            src="/img-assets/double-check.svg"
                                                            alt="check"
                                                            sx={{ width: '20px', height: '20px', flexShrink: 0 }}
                                                        />
                                                        <Typography sx={{ color: '#000000', fontSize: { xs: '0.9rem', md: '1rem' }, lineHeight: 1.5 }}>
                                                            <Lang>{text}</Lang>
                                                        </Typography>
                                                    </Box>
                                                ))
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Container>
                    </Box>

                    {/* Benefits Section */}
                    <Box sx={{ bgcolor: 'white', py: { xs: 8, md: 12 } }}>
                        <Container maxWidth="lg">
                            <Box sx={{ textAlign: 'center', mb: 8 }}>
                                <Typography
                                    variant="h2"
                                    component="h2"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '1.3rem', md: '1.75rem', lg: '2.2rem' },
                                        mb: 2,
                                        color: '#1a1a1a',
                                    }}
                                >
                                    <Lang>Πλεονεκτήματα|Benefits</Lang>
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: '#6b7280',
                                        width: '100%',
                                        fontWeight: 400,
                                    }}
                                >
                                    <Lang>
                                        Γιατί να επιλέξετε τη πλατφόρμα μας|Why choose our platform
                                    </Lang>
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                                gap: 4,
                            }}>
                                {[
                                    {
                                        icon: 'fast-track.svg',
                                        title: 'Lunchbox Fast Track|Lunchbox Fast Track',
                                        description: 'Με την προπαραγγελία, οι μαθητές παραλαμβάνουν γρήγορα τα γεύματά τους από ξεχωριστό σημείο παράδοσης, μειώνοντας τις ουρές και κερδίζοντας χρόνο διαλείμματος.|With pre-ordering, students receive their meals quickly from a separate delivery point, reducing queues and gaining break time.',
                                    },
                                    {
                                        icon: 'e-order-mgnt.svg',
                                        title: 'e-Διαχείριση Παραγγελιών|e-Order Management',
                                        description: 'Η Ηλεκτρονική διαχείριση των παραγγελιών μηδενίζει τα σφάλματα στην εταιρεία σίτισης και τις ενδοσχολικές μονάδες εστίασης. Παράγονται λίστες με τις λεπτομερείς παραγγελίες της ημέρας ανά τάξη, ανά τμήμα, κτλ. για τη σωστή παράδοση από τις τοπικές μονάδες.|Electronic order management eliminates errors in the catering company and school-based food service units. Lists are generated with detailed daily orders by class, by section, etc. for proper delivery from local units.',
                                    },
                                    {
                                        icon: 'meal-options.svg',
                                        title: 'Επιλογές Γευμάτων|Meal Options',
                                        description: 'Οι μαθητές μπορούν να επιλέξουν εναλλακτικά γεύματα για τη σίτισή τους και να μην υπάρξει καμία μέρα που να χάσουν το γεύμα τους|Students can choose alternative meals so there will be no day that they miss their meal',
                                    }
                                ].map((benefit, index) => {
                                    return (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                textAlign: 'center',
                                            }}
                                        >
                                            {/* Icon */}
                                            <Box
                                                component="img"
                                                src={`/img-assets/${benefit.icon}`}
                                                alt={benefit.title.split('|')[0]}
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    mb: 3,
                                                }}
                                            />

                                            {/* Title */}
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#E35827',
                                                    mb: 2,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                <Lang>{benefit.title}</Lang>
                                            </Typography>

                                            {/* Description */}
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    color: '#6b7280',
                                                    lineHeight: 1.6,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                <Lang>{benefit.description}</Lang>
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Container>
                    </Box>

                    {/* Contact Form Section - Shopify Style */}
                    <Box id="contact" sx={{ bgcolor: '#F5F7FA', py: { xs: 8, md: 12 }, overflowX: 'hidden' }}>
                        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
                            <Box sx={{ textAlign: 'center', mb: 6 }}>
                                <Typography
                                    variant="h2"
                                    component="h2"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '1.3rem', md: '1.75rem', lg: '2.2rem' },
                                        mb: 2,
                                        color: '#1a1a1a',
                                    }}
                                >
                                    <Lang>Επικοινωνήστε Μαζί Μας|Contact Us</Lang>
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: '#6b7280',
                                        fontWeight: 400,
                                    }}
                                >
                                    <Lang>
                                        Έτοιμοι να ξεκινήσετε; Επικοινωνήστε μαζί μας σήμερα για να προγραμματίσετε μια σύντομη διαδικτυακή επίδειξη 15 λεπτών.|Ready to get started? Contact us today to schedule a brief 15-minute online demo.
                                    </Lang>
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                gap: { xs: 4, md: 6 },
                                alignItems: { xs: 'center', md: 'stretch' },
                                justifyContent: { xs: 'center', md: 'center' },
                            }}>
                                {/* Form on the left */}
                                <Box sx={{
                                    width: '100%',
                                    maxWidth: { xs: '100%', md: '500px' },
                                    order: { xs: 1, md: 1 },
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}>
                                    <form onSubmit={handleContactSubmit} noValidate style={{ width: '100%' }}>
                                        <Stack spacing={2} sx={{ alignItems: 'center', width: '100%', maxWidth: '100%' }}>
                                            <FormControl required sx={{ width: { xs: '100%', sm: '400px' }, maxWidth: '100%' }}>
                                                <InputLabel>{fromLabel}</InputLabel>
                                                <Select
                                                    required
                                                    value={contactForm.from || ''}
                                                    label={fromLabel}
                                                    onChange={(e) => handleContactFormChange('from', e.target.value)}
                                                    IconComponent={(props) => (
                                                        <ExpandMoreIcon {...props} sx={{ color: '#4b5563' }} />
                                                    )}
                                                    sx={{
                                                        borderRadius: '8px',
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderRadius: '8px',
                                                        },
                                                    }}
                                                >
                                                    <MenuItem value="school">
                                                        <Lang>Σχολείο|School</Lang>
                                                    </MenuItem>
                                                    <MenuItem value="catering">
                                                        <Lang>Εταιρεία Σίτισης|Catering Company</Lang>
                                                    </MenuItem>
                                                    <MenuItem value="parentAssociation">
                                                        <Lang>Σύλλογος γονέων|Parent Association</Lang>
                                                    </MenuItem>
                                                    <MenuItem value="parent">
                                                        <Lang>Γονέας|Parent</Lang>
                                                    </MenuItem>
                                                </Select>
                                            </FormControl>

                                            <TextField
                                                required
                                                label={nameLabel}
                                                value={contactForm.name}
                                                onChange={(e) => handleContactFormChange('name', e.target.value)}
                                                sx={{
                                                    width: { xs: '100%', sm: '400px' },
                                                    maxWidth: '100%',
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                    },
                                                }}
                                            />

                                            <Box sx={{ width: { xs: '100%', sm: '400px' }, maxWidth: '100%' }}>
                                                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, alignItems: 'flex-start', flexWrap: 'nowrap' }}>
                                                    <Autocomplete
                                                        options={countryCodes}
                                                        value={countryCodes.find(c => c.code === contactForm.countryCode) || countryCodes[0]}
                                                        onChange={(_, newValue) => {
                                                            if (newValue) {
                                                                handleContactFormChange('countryCode', newValue.code);
                                                            }
                                                        }}
                                                        fullWidth={false}
                                                        getOptionLabel={(option) => typeof option === 'string' ? option : `${option.code} ${option.name}`}
                                                        filterOptions={(options, { inputValue }) => {
                                                            if (!inputValue) return options;
                                                            return options.filter((option) =>
                                                                option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                                option.code.includes(inputValue)
                                                            );
                                                        }}
                                                        renderOption={(props, option) => (
                                                            <Box component="li" {...props}>
                                                                <Box component="span" sx={{ mr: 1 }}>{option.flag}</Box>
                                                                <Box component="span" sx={{ mr: 1, fontWeight: 500 }}>{option.code}</Box>
                                                                <Box component="span" sx={{ color: 'text.secondary' }}>{option.name}</Box>
                                                            </Box>
                                                        )}
                                                        renderInput={(params) => {
                                                            const selectedCountry = countryCodes.find(c => c.code === contactForm.countryCode);
                                                            return (
                                                                <TextField
                                                                    {...params}
                                                                    label={mobileLabel}
                                                                    placeholder="Search"
                                                                    fullWidth={false}
                                                                    slotProps={{
                                                                        ...params.slotProps,
                                                                        inputLabel: {
                                                                            ...params.slotProps.inputLabel,
                                                                            sx: {
                                                                                '&.Mui-focused': {
                                                                                    color: '#01ABAE',
                                                                                },
                                                                                '&.MuiInputLabel-shrink': {
                                                                                    color: '#01ABAE',
                                                                                },
                                                                            },
                                                                        },
                                                                        input: {
                                                                            ...params.slotProps.input,
                                                                            startAdornment: selectedCountry ? (
                                                                                <>
                                                                                    <Box component="span" sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>
                                                                                        {selectedCountry.flag}
                                                                                    </Box>
                                                                                    {params.slotProps.input.startAdornment}
                                                                                </>
                                                                            ) : params.slotProps.input.startAdornment,
                                                                        },
                                                                        htmlInput: {
                                                                            ...params.slotProps.htmlInput,
                                                                            style: {
                                                                                padding: '8.5px 14px',
                                                                            },
                                                                        },
                                                                    }}
                                                                    sx={{
                                                                        width: '100%',
                                                                        '& .MuiInputBase-input': {
                                                                            fontSize: { xs: '12px', sm: '14px' },
                                                                        },
                                                                    }}
                                                                />
                                                            );
                                                        }}
                                                        sx={{
                                                            flex: { xs: '0 1 45%', sm: 1 },
                                                            minWidth: 0,
                                                            maxWidth: { xs: '45%', sm: 'none' },
                                                            width: { xs: '45%', sm: '100%' },
                                                            '&.MuiAutocomplete-root': {
                                                                width: { xs: '45%', sm: '100%' },
                                                            },
                                                            '& .MuiFormControl-root': {
                                                                width: '100%',
                                                            },
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: '8px',
                                                                height: '40px',
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#01ABAE',
                                                                },
                                                            },
                                                        }}
                                                        size="small"
                                                        openOnFocus
                                                        disableClearable
                                                        clearOnBlur={false}
                                                        slotProps={{
                                                            popper: {
                                                                sx: {
                                                                    zIndex: 1300,
                                                                    '& .MuiAutocomplete-paper': {
                                                                        minWidth: { xs: '280px', sm: '320px' },
                                                                        width: { xs: '280px', sm: 'auto' },
                                                                    },
                                                                },
                                                                placement: 'bottom-start',
                                                                modifiers: [
                                                                    {
                                                                        name: 'offset',
                                                                        options: {
                                                                            offset: [0, 4],
                                                                        },
                                                                    },
                                                                ],
                                                            },
                                                        }}
                                                    />
                                                    <TextField
                                                        value={contactForm.mobile}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(/\D/g, '');
                                                            handleContactFormChange('mobile', value);
                                                        }}
                                                        type="tel"
                                                        label={mobileNumberPlaceholder}
                                                        slotProps={{
                                                            htmlInput: {
                                                                inputMode: 'numeric',
                                                            },
                                                            inputLabel: {
                                                                sx: {
                                                                    '&.Mui-focused': {
                                                                        color: '#01ABAE',
                                                                    },
                                                                    '&.MuiInputLabel-shrink': {
                                                                        color: '#01ABAE',
                                                                    },
                                                                },
                                                            },
                                                        }}
                                                        sx={{
                                                            flex: { xs: '1 1 55%', sm: 1 },
                                                            minWidth: 0,
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: '8px',
                                                                height: '40px',
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: '#01ABAE',
                                                                },
                                                            },
                                                        }}
                                                        size="small"
                                                    />
                                                </Box>
                                            </Box>

                                            <TextField
                                                required
                                                label={emailLabel}
                                                type="email"
                                                value={contactForm.email}
                                                onChange={(e) => handleContactFormChange('email', e.target.value)}
                                                sx={{
                                                    width: { xs: '100%', sm: '400px' },
                                                    maxWidth: '100%',
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                    },
                                                }}
                                            />

                                            <TextField
                                                label={messageLabel}
                                                value={contactForm.message}
                                                onChange={(e) => handleContactFormChange('message', e.target.value)}
                                                multiline
                                                rows={6}
                                                sx={{
                                                    width: { xs: '100%', sm: '400px' },
                                                    maxWidth: '100%',
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: '8px',
                                                    },
                                                }}
                                            />

                                            {submitError && (
                                                <Alert severity="error" sx={{ borderRadius: '8px' }}>
                                                    {submitError}
                                                </Alert>
                                            )}

                                            {submitSuccess && (
                                                <Alert severity="success" sx={{ borderRadius: '8px' }}>
                                                    <Lang>
                                                        Το μήνυμά σας στάλθηκε επιτυχώς! Θα επικοινωνήσουμε μαζί σας σύντομα.|Your message has been sent successfully! We will contact you soon.
                                                    </Lang>
                                                </Alert>
                                            )}

                                            <Button
                                                type="submit"
                                                variant="contained"
                                                size="large"
                                                disabled={submitting}
                                                sx={{
                                                    width: { xs: '100%', sm: '400px' },
                                                    maxWidth: '100%',
                                                    height: '64px',
                                                    borderRadius: '16px',
                                                    fontSize: '1.1rem',
                                                    fontWeight: 600,
                                                    textTransform: 'none',
                                                    bgcolor: '#01ABAE',
                                                    touchAction: 'manipulation',
                                                    WebkitTapHighlightColor: 'transparent',
                                                    '&:hover': {
                                                        bgcolor: '#01999c',
                                                    },
                                                    '&:active': {
                                                        bgcolor: '#01999c',
                                                    },
                                                }}
                                            >
                                                {submitting ? sendingButton : sendButton}
                                            </Button>
                                        </Stack>
                                    </form>
                                </Box>

                                {/* Image on the right */}
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    order: { xs: 2, md: 2 },
                                    height: { xs: 'auto', md: '100%' },
                                    width: { xs: '100%', md: 'auto' },
                                }}>
                                    <Box
                                        component="img"
                                        src="/img-assets/contact-us.png"
                                        alt="Contact us"
                                        sx={{
                                            width: '100%',
                                            maxWidth: '500px',
                                            height: { xs: 'auto', md: '100%' },
                                            objectFit: { xs: 'contain', md: 'contain' },
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Container>
                    </Box>

                    {/* FAQ Section - Shopify Style */}
                    <Box sx={{ bgcolor: '#ffffff', py: { xs: 8, md: 12 } }}>
                        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
                            <Box sx={{ textAlign: 'center', mb: 6 }}>
                                <Typography
                                    variant="h2"
                                    component="h2"
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: '1.3rem', md: '1.75rem', lg: '2.2rem' },
                                        mb: 2,
                                        color: '#1a1a1a',
                                    }}
                                >
                                    <Lang>Συχνές Ερωτήσεις|Frequently Asked Questions</Lang>
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                gap: { xs: 4, md: 6 },
                                alignItems: { xs: 'center', md: 'flex-start' },
                            }}>
                                {/* Image on the left */}
                                <Box sx={{
                                    flex: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    order: { xs: 2, md: 1 },
                                }}>
                                    <Box
                                        component="img"
                                        src="/img-assets/faq.png"
                                        alt="FAQ"
                                        sx={{
                                            width: '100%',
                                            maxWidth: '500px',
                                            height: 'auto',
                                        }}
                                    />
                                </Box>

                                {/* Questions on the right */}
                                <Box sx={{
                                    flex: 1,
                                    width: '100%',
                                    order: { xs: 1, md: 2 },
                                }}>
                                    <Stack spacing={2}>
                                        {[
                                            {
                                                question: 'Τι είναι η πλατφόρμα Lunchbox;|What is the Lunchbox platform?',
                                                answer: 'Η πλατφόρμα Lunchbox είναι μια ηλεκτρονική πλατφόρμα παραγγελιών γευμάτων εξειδικευμένη για σχολεία. Συνεργαζόμαστε με εταιρείες σίτισης ή απευθείας με εστιατόρια σχολείων, για να προσφέρουμε την εμπειρία της ηλεκτρονικής παραγγελίας στους γονείς και μαθητές.|Lunchbox platform is an electronic meal ordering platform for schools and catering companies. We work with catering companies or directly with school restaurants, to provide the experience of electronic ordering to parents and students.',
                                            },
                                            {
                                                question: 'Ποιοι μπορούν να χρησιμοποιήσουν την πλατφόρμα;|Who can use the platform?',
                                                answer: 'Η πλατφόρμα απευθύνεται σε γονείς μαθητών που φοιτούν σε συνεργαζόμενα με εμάς σχολεία. Η σχολική διοίκηση και οι πάροχοι γευμάτων χρησιμοποιούν την πλατφόρμα για θεσμική διαχείριση παραγγελιών και πακέτων σίτισης, τόσο για τη σωστή προετοιμασία των γευμάτων, όσο και για την έγκαιρη και χωρίς λάθη παράδοσή τους.|Our platform serves parents of students at our partner schools. School administration and catering companies use the platform for institutional management of orders and meal plans, both for correct meal preparation and timely, error-free delivery.',
                                            },
                                            {
                                                question: 'Πώς λειτουργεί η πλατφόρμα;|How does the platform work?',
                                                answer: 'Οι γονείς δημιουργούν λογαριασμό, επιλέγουν γεύματα ή κάποιο πακέτο σίτισης και πληρώνουν online. Την ώρα του διαλείμματος, οι μαθητές παραλαμβάνουν γρήγορα τα προπληρωμένα γεύματά τους από τα εστιατόρια των σχολείων. Εάν κάποια μέρα ο μαθητής δεν επιθυμεί το γεύμα του πακέτου σίτισης, μπορεί να επιλέξει κάποιο άλλο γεύμα που προσφέρεται εκείνη την ημέρα. |Parents create an account, select meals or a meal plan, and pay online. During lunch break, students receive their pre-ordered meals from the school restaurants. If for some reason the student does not want the meal of the meal plan, he can choose another meal that is offered that day.',
                                            },
                                            {
                                                question: 'Ποιο είναι το κόστος για τη χρήση της πλατφόρμας;|What is the cost for using the platform?',
                                                answer: 'Δεν υπάρχει κόστος για τους γονείς. Για τους πάροχους γευμάτων — είτε εταιρείες σίτισης είτε σχολεία με εσωτερικές κουζίνες — το κόστος εξαρτάται από παράγοντες όπως το μέγεθος του σχολείου, τον αριθμό των μαθητών και τις απαιτούμενες λειτουργίες. Επικοινωνήστε μαζί μας για να μάθετε περισσότερα σχετικά με τις τιμές και τα πακέτα.|There is no cost for parents. For food providers — whether catering companies or schools that prepare meals in-house — the cost depends on factors such as school size, number of students, and features required. Contact us to learn more about pricing and packages.',
                                            },
                                        ].map((faq, index) => (
                                            <Accordion
                                                key={index}
                                                elevation={0}
                                                sx={{
                                                    bgcolor: '#F5F7FA',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '16px !important',
                                                    '&:before': { display: 'none' },
                                                    '&.Mui-expanded': {
                                                        marginTop: 2,
                                                        marginBottom: 0,
                                                    },
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '24px',
                                                            height: '24px',
                                                            borderRadius: '50%',
                                                            bgcolor: '#D9D9D9',
                                                            color: 'white',
                                                            position: 'relative',
                                                            '& .add-icon': {
                                                                display: 'block',
                                                            },
                                                            '& .remove-icon': {
                                                                display: 'none',
                                                            },
                                                        }}>
                                                            <AddIcon sx={{
                                                                fontSize: '18px',
                                                                position: 'absolute',
                                                                display: 'block',
                                                            }} />
                                                            <RemoveIcon sx={{
                                                                fontSize: '18px',
                                                                position: 'absolute',
                                                                display: 'none',
                                                            }} />
                                                        </Box>
                                                    }
                                                    sx={{
                                                        px: 3,
                                                        py: 2,
                                                        '&.Mui-expanded': {
                                                            minHeight: 'auto',
                                                            '& .MuiAccordionSummary-expandIconWrapper': {
                                                                '& .MuiSvgIcon-root:first-of-type': {
                                                                    display: 'none',
                                                                },
                                                                '& .MuiSvgIcon-root:last-of-type': {
                                                                    display: 'block',
                                                                },
                                                            },
                                                        },
                                                        '& .MuiAccordionSummary-expandIconWrapper': {
                                                            '& .MuiSvgIcon-root:first-of-type': {
                                                                display: 'block',
                                                            },
                                                            '& .MuiSvgIcon-root:last-of-type': {
                                                                display: 'none',
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                                                        <Lang>{faq.question}</Lang>
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                                                    <Typography variant="body1" sx={{ color: '#6b7280', lineHeight: 1.6 }}>
                                                        <Lang>{faq.answer}</Lang>
                                                    </Typography>
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                    </Stack>
                                </Box>
                            </Box>
                        </Container>
                    </Box>

                    {/* Footer - Shopify Style */}
                    <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: { xs: 3, md: 4 } }}>
                        <Container maxWidth="lg">
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                alignItems: 'center',
                                textAlign: 'center',
                            }}>
                                <Box
                                    component="img"
                                    src="/img-assets/logo-dark.svg"
                                    alt="Lunchbox"
                                    sx={{
                                        height: { xs: 32, md: 40 },
                                        display: 'block',
                                    }}
                                />
                                <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                                    <Lang>
                                        {`Copyright © ${new Date().getFullYear()} Lunchbox. Όλα τα δικαιώματα διατηρούνται.|Copyright © ${new Date().getFullYear()} Lunchbox. All rights reserved.`}
                                    </Lang>
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'center' }}>
                                    <Typography
                                        component="a"
                                        href="/terms/privacy-landing.html"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="body2"
                                        sx={{
                                            color: '#9ca3af',
                                            textDecoration: 'underline',
                                            '&:hover': {
                                                color: '#01ABAE',
                                            },
                                        }}
                                    >
                                        <Lang>Πολιτική Απορρήτου|Privacy Policy</Lang>
                                    </Typography>
                                    <Typography
                                        component="a"
                                        href="/terms/cookies.html"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        variant="body2"
                                        sx={{
                                            color: '#9ca3af',
                                            textDecoration: 'underline',
                                            '&:hover': {
                                                color: '#01ABAE',
                                            },
                                        }}
                                    >
                                        <Lang>Πολιτική Cookies|Cookies Policy</Lang>
                                    </Typography>
                                </Box>
                            </Box>
                        </Container>
                    </Box>

                    {/* Cookies Consent */}
                    <CookiesConsent onConsent={(accepted) => setCookiesConsented(accepted)} />
                </Box>
            </ThemeProvider>
        </langContext.Provider>
    );
}
